import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'dashboard-theme';
const DARK = 'dark';
const LIGHT = 'light';
const MINIMALIST = 'minimalist';

const ThemeContext = createContext({
    theme: DARK,
    setTheme: () => { },
    toggleTheme: () => { },
});

function resolveInitialTheme() {
    if (typeof window === 'undefined') {
        return DARK;
    }

    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === DARK || stored === LIGHT || stored === MINIMALIST) {
        return stored;
    }

    const prefersLight = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches;
    return prefersLight ? LIGHT : DARK;
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(resolveInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = theme;
        try {
            window.localStorage.setItem(STORAGE_KEY, theme);
        } catch (error) {
            // Ignore persistence errors (e.g., private mode).
        }
    }, [theme]);

    useEffect(() => {
        const root = document.documentElement;
        root.dataset.theme = theme;
    }, [theme]);

    const value = useMemo(
        () => ({
            theme,
            setTheme,
            toggleTheme: () => setTheme((current) => {
                if (current === DARK) return LIGHT;
                if (current === LIGHT) return MINIMALIST;
                return DARK;
            }),
        }),
        [theme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

export const THEMES = { DARK, LIGHT, MINIMALIST };
